import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useProductCodes } from "@/features/ledger/api/use-product-codes";
import type { ProductCodeFilter } from "@/features/ledger/api/dto";
import {
  ProductCodeFormDialog,
  productCodesColumns,
  type ProductCodeRow,
} from "@/features/ledger/components";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import type { ProductCategory } from "@/features/shared/api/enums";

/** Traduit les filtres de colonnes DataTableClar en `ProductCodeFilter` (§4.2). */
const toProductCodeFilter = (
  filters: ColumnFiltersState
): ProductCodeFilter => {
  const filter: ProductCodeFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "code") filter.code = value;
    if (id === "category") filter.category = value as ProductCategory;
  }
  return filter;
};

/**
 * Codes produits paginés (`POST /ProductCodes/all`, guide §6.4) — lecture
 * authentifiée ; écriture réservée à flashpay-superadmin / flashpay-admin
 * (désactivation : superadmin uniquement).
 */
export function ProductCodesPage() {
  const { t } = useLedgerTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toProductCodeFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useProductCodes(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("ledger.productCodes.title")}
        </h1>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("ledger.productCodes.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((r): r is ProductCodeRow =>
          Boolean(r.id)
        )}
        columnsClar={productCodesColumns}
        title={t("ledger.productCodes.title")}
        exportFileName="product-codes"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <ProductCodeFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
